"use client";
import React, {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useId,
} from "react";

import { Textarea, TextAreaProps } from "@nextui-org/react";

// NextUI uses HTMLInputElement for Textarea, which is semantically incorrect
// Create type that accounts for this feature
export interface BaseTextareaProps
  extends Omit<
    TextAreaProps,
    | "isInvalid"
    | "errorMessage"
    | "label"
    | "required"
    | "id"
    | "onFocus"
    | "onChange"
    | "onBlur"
  > {
  label?: string;
  error?: string;
  autoSelect?: boolean;
  required?: boolean;
  labelRightComponent?: React.ReactNode;
  errorClassName?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
}

/**
 * Universal form textarea component with consistent styling.
 */
const BaseTextareaComponent = (
  {
    label,
    error,
    autoSelect,
    required = false,
    labelRightComponent,
    onFocus,
    onChange,
    onBlur,
    className,
    errorClassName,
    ...textareaProps
  }: BaseTextareaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) => {
  const inputId = useId();

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (
        autoSelect &&
        event.currentTarget &&
        typeof event.currentTarget.select === "function"
      ) {
        event.currentTarget.select();
      }

      if (onFocus) {
        onFocus(event);
      }
    },
    [autoSelect, onFocus],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
    },
    [onBlur],
  );

  return (
    <Textarea
      ref={ref}
      id={inputId}
      label={label}
      labelPlacement="outside"
      placeholder=" "
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      isInvalid={!!error}
      isRequired={required}
      disableAnimation={true}
      classNames={{
        base: "form-textarea-base no-focus-ring",
        mainWrapper: "form-textarea-wrapper",
        input: "form-textarea-inner",
        innerWrapper: "form-textarea-wrapper",
        inputWrapper: "form-textarea-container",
        label: `form-label ${error ? "text-danger" : ""}`,
        errorMessage: `form-error-message ${errorClassName || ""}`,
      }}
      className={className}
      {...textareaProps}
    />
  );
};

const BaseTextarea = memo(forwardRef(BaseTextareaComponent));

// Add displayName for debugging
BaseTextarea.displayName = "BaseTextarea";

export default BaseTextarea;
